import { useLayoutEffect } from "react";
import { RegisterOptions, useController } from "react-hook-form";
import BaseTimezoneSelect from "components/BaseTimezoneSelect";
import { useFloatingField } from "./context";
import { sizeStyles } from "./sizeStyles";

type FloatingTimezoneProps = {
  control: any;
  name: string;
  rules?: RegisterOptions;
  setDefault?: boolean;
  placeholder?: string;
  disabled?: boolean;
  onBlur?: () => void;
};

function FloatingTimezone({
  control,
  name,
  rules,
  setDefault = false,
  placeholder = "",
  disabled,
  onBlur: onBlurProp,
}: FloatingTimezoneProps) {
  const { setHasValue, size } = useFloatingField();
  const styles = sizeStyles[size];
  const {
    field: { onChange, onBlur, value },
  } = useController({
    name,
    control,
    rules,
    ...(setDefault ? { defaultValue: Intl.DateTimeFormat().resolvedOptions().timeZone } : {}),
  });

  useLayoutEffect(() => {
    setHasValue(!!value);
  }, [value, setHasValue]);

  // BaseTimezoneSelect with size="sm" forces maxHeight: 32px on the control, but our sm
  // inputHeight is 40px. Override control height to match the FloatingField size.
  // Vertical positioning of the value text is handled by FloatingField's sx rules
  // (paddingTop on the value-container, not on singleValue).
  const timezoneStyles = {
    control: (provided: any) => ({
      ...provided,
      // Preserve the essential base styles that BaseTimezoneSelect spreads from
      // chakraTheme.components.Input.baseStyle.field (resets + box model)
      outline: 0,
      position: "relative",
      appearance: "none",
      boxShadow: "none",
      cursor: "pointer",
      backgroundColor: "transparent",
      fontSize: styles.fontSize,
      minHeight: styles.inputHeight,
      height: styles.inputHeight,
      maxHeight: styles.inputHeight,
      padding: "0",
      borderRadius: "var(--chakra-radii-lg)",
    }),
    singleValue: (provided: any) => ({
      ...provided,
      color: "var(--chakra-colors-fg-muted)",
      backgroundColor: "transparent",
      // paddingTop is intentionally omitted — value-container paddingTop in FloatingField's sx
      // handles vertical positioning so text clears the floated label. Setting it here would
      // stack with the container paddingTop and push text below the visible area.
    }),
  };

  return (
    <BaseTimezoneSelect
      value={value ?? ""}
      onBlur={() => {
        onBlur();
        onBlurProp?.();
      }}
      onChange={(event: any) => {
        onChange(event.value);
        setHasValue(!!event.value);
      }}
      name={name}
      size={size}
      placeholder={placeholder}
      isDisabled={disabled}
      styles={timezoneStyles}
    />
  );
}

export default FloatingTimezone;
